import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MAX_FILE_BYTES } from '@goyone/shared';
import { api, upload as uploadFile } from '../lib/api';
import { desktop } from '../lib/desktop';
import { deskRecorder } from '../lib/deskRecorder';
import { useDesktopPresence } from '../lib/desktopPresence';
import { useToast } from './Toast';
import { useScreenMonitor } from './ScreenMonitor';

/**
 * جلسة تسجيل الوردية — كانت حبيسة الكونسول وصارت مشتركة.
 *
 * لوحة الاتصال الراسية تتصل من مكانها في أي صفحة، وسياسة التسجيل
 * الملزمة يجب أن تلحقها لا أن تحيلها: فالخطّاف استُخرج من الكونسول
 * كما هو — يطلب مشاركة الشاشة بصوت النظام مرةً، يسلّم المسار لمركز
 * المراقبة، يمزج الميكروفون، ويسجّل كل مكالمة ويرفعها إلى ملفها.
 *
 * كل مُركِّبٍ للخطّاف جلستُه مستقلة تنتهي بإغلاقه — الميكروفون لا
 * يبقى مفتوحًا خلسةً بعد إغلاق اللوحة أو مغادرة الكونسول.
 *
 * ## وبابان لا باب
 *
 * حين يعمل الموظّفُ من المتصفّحِ والتطبيقُ نشطٌ على جهازه، يسجّل
 * التطبيقُ ويسلّم الصوتَ لهذه الصفحةِ لترفعَه بجلستها — فلا نافذةَ
 * مشاركةٍ ولا سؤالَ كلَّ يوم. وإن لم يكن التطبيقُ هناك فالمتصفّحُ كما
 * كان. والمراقبةُ لا تتبع البابَ الأوّل: هي تحتاج مسارَ صورةٍ في هذه
 * الصفحة، ولا صورةَ تعبرُ الجسر — فتبقى على إذنها الخاصّ.
 */
export function useShiftRecorder() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const monitor = useScreenMonitor();
  const presence = useDesktopPresence();
  const [session, setSession] = useState<null | {
    hasSystemAudio: boolean;
    via: 'browser' | 'app';
  }>(null);

  /* سياسة الإدارة: هل التسجيل ملزم؟ وهل المراقبة مفعّلة وبأي وضع؟ */
  const policyQuery = useQuery({
    queryKey: ['cc', 'monitor-policy'],
    queryFn: () =>
      api<{ callRecording: boolean; mode: 'OFF' | 'SCREENSHOTS' | 'VIDEO'; screenshotEverySec: number }>(
        '/callcenter/monitoring/policy',
      ),
    staleTime: 60_000,
  });
  const policy = policyQuery.data ?? {
    callRecording: false,
    mode: 'OFF' as const,
    screenshotEverySec: 60,
  };

  const mixedRef = useRef<MediaStream | null>(null);
  const cleanupRef = useRef<() => void>(() => undefined);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  /* صوتُ مكالمةٍ أُقفلت وتنتظرُ نتيجتَها لتُرفع. */
  const pendingRef = useRef<Blob | null>(null);
  /* البابُ الذي فُتحت به الجلسة — تقرؤه المعالجاتُ بعد انتهاء العرض. */
  const viaRef = useRef<'browser' | 'app'>('browser');

  /* حضورُ التسجيل فقط؛ علمُ المراقبة يضبطه مركز المراقبة عند تبنّيه المسار. */
  const flags = (recordingOn: boolean) =>
    api('/callcenter/presence/flags', { method: 'PATCH', body: { recordingOn } }).catch(
      () => undefined,
    );

  const stopShift = () => {
    if (viaRef.current === 'app') void deskRecorder.stop();
    recorderRef.current?.state !== 'inactive' && recorderRef.current?.stop();
    recorderRef.current = null;
    chunksRef.current = [];
    pendingRef.current = null;
    /* المراقبة والمشاهدة الحيّة يوقفهما مركزُها — نحن سلّمناه المسار. */
    monitor.detach();
    cleanupRef.current();
    mixedRef.current = null;
    viaRef.current = 'browser';
    setSession(null);
    void flags(false);
  };

  /**
   * التسجيلُ من التطبيق — يُجرَّب أوّلًا حين يكون نشطًا على الجهاز.
   *
   * وإخفاقُه لا يُوقف الوردية: يُرجَع إلى المتصفّحِ بنافذتِه. فالتطبيقُ
   * راحةٌ لا شرط، ومن أُغلق تطبيقُه في منتصفِ اليوم لا يُحرم التسجيل.
   */
  const startViaApp = async (): Promise<boolean> => {
    const reply = await deskRecorder.start();
    if (!reply.ok) return false;
    viaRef.current = 'app';
    setSession({ hasSystemAudio: true, via: 'app' });
    void flags(true);
    toast(
      policy.mode === 'OFF'
        ? 'بدأت الجلسة — يسجّل تطبيقُ سطح المكتب المكالمات بلا إذن مشاركة'
        : 'بدأت الجلسة — يسجّل التطبيقُ المكالمات، ومراقبةُ الشاشة تحتاج إذنَها من هذه الصفحة',
    );
    return true;
  };

  const startShift = async (): Promise<boolean> => {
    if (mixedRef.current || viaRef.current === 'app') return true;

    /* في التطبيق نفسِه لا حاجةَ للجسر — الصفحةُ تنال الشاشةَ بلا سؤال. */
    if (!desktop && presence.active && (await startViaApp())) return true;

    try {
      const display = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: true,
      });

      if (display.getAudioTracks().length === 0) {
        display.getTracks().forEach((t) => t.stop());
        /* والنصيحةُ بحسب البابِ — «اختر الشاشة كاملة» لا معنى له في التطبيق. */
        toast(
          desktop
            ? 'تعذّر التقاط صوت النظام من التطبيق — تأكّد من أنّ مخرج الصوت يعمل ثم أعد المحاولة'
            : 'لم تُشارَك أصوات النظام — اختر «الشاشة كاملة» وفعّل «مشاركة صوت النظام» ثم أعد المحاولة',
        );
        return false;
      }

      /* خامًا بلا معالجة — لئلّا يُنازع المكالمةَ على الميكروفون. */
      const mic = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      });

      /*
       * الشاشة تُسلَّم إلى مركز المراقبة — مصدرٌ واحد للالتقاط والمشاهدة الحيّة.
       *
       * المتصفح لا يعطي صوت النظام إلا عبر نافذة مشاركة الشاشة، فمسار الفيديو
       * يصل حاملًا اضطراريًّا مع الصوت الذي نحتاجه لتسجيل المكالمة. وبدل أن
       * يلتقط المُركِّب من المسار بنفسه (فيصير للمراقبة موضعان ومشاركتان)، يتبنّى
       * مركزُ المراقبة المسارَ فيجري عليه لقطاتِ السياسة والمشاهدةَ الحيّة —
       * وإيقافُه من الشريط العلوي يُنهي هذه الجلسة عبر stopShift.
       */
      monitor.attach(display, stopShift);

      const ctx = new AudioContext();
      const dest = ctx.createMediaStreamDestination();
      ctx.createMediaStreamSource(display).connect(dest);
      ctx.createMediaStreamSource(mic).connect(dest);

      cleanupRef.current = () => {
        display.getTracks().forEach((t) => t.stop());
        mic.getTracks().forEach((t) => t.stop());
        void ctx.close();
      };
      mixedRef.current = dest.stream;

      viaRef.current = 'browser';
      setSession({ hasSystemAudio: true, via: 'browser' });
      void flags(true);
      toast(
        policy.mode === 'OFF'
          ? 'بدأت الجلسة — يُلتقط الصوت فقط، الشاشة لا تُسجَّل'
          : policy.mode === 'SCREENSHOTS'
            ? `بدأت الجلسة — تُسجَّل المكالمات وتُرسل لقطة شاشة كل ${policy.screenshotEverySec} ثانية`
            : 'بدأت الجلسة — تُسجَّل المكالمات ويُسجَّل فيديو الوردية',
      );
      return true;
    } catch {
      /* أغلق نافذة الاختيار — لا خطأ يُصرخ به. */
      return false;
    }
  };

  const beginCall = () => {
    if (viaRef.current === 'app') {
      void deskRecorder.beginCall();
      return;
    }
    const stream = mixedRef.current;
    if (!stream || recorderRef.current) return;
    const rec = new MediaRecorder(stream, {
      mimeType: 'audio/webm;codecs=opus',
      /* صوت كلام لا موسيقى: 32kbps تكفي وتُبقي ساعةً كاملة دون حدّ العشرة ميجا. */
      audioBitsPerSecond: 32_000,
    });
    chunksRef.current = [];
    rec.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    rec.start(1000);
    recorderRef.current = rec;
  };

  /** الصوتُ من بابِه، ثمّ الرفعُ واحدٌ للبابين. */
  const collect = async (): Promise<Blob | null> => {
    if (viaRef.current === 'app') return deskRecorder.endCall();

    const rec = recorderRef.current;
    recorderRef.current = null;
    if (!rec) return null;
    if (rec.state !== 'inactive') {
      await new Promise<void>((res) => {
        rec.addEventListener('stop', () => res(), { once: true });
        rec.stop();
      });
    }
    const chunks = chunksRef.current;
    chunksRef.current = [];
    if (chunks.length === 0) return null;
    return new Blob(chunks, { type: 'audio/webm' });
  };

  /**
   * يُوقف التسجيلَ لحظةَ إقفالِ المكالمة، ويحتفظُ بالصوتِ حتى تُسجَّل النتيجة.
   *
   * وإلّا لسجّل الشريطُ ما بعدَ الإقفال: صمتَ الموظّفِ وهو يكتبُ النتيجةَ
   * في نافذةِ الإنهاء، وما يقوله لمن بجواره وهو يظنّ المكالمةَ انتهت.
   * فالشريطُ يُقفل مع الخطّ، والرفعُ ينتظرُ النتيجة.
   */
  const haltCall = async () => {
    if (pendingRef.current) return;
    pendingRef.current = await collect();
  };

  const finishCall = async (callId: string, status: string) => {
    const blob = pendingRef.current ?? (await collect());
    pendingRef.current = null;
    /* الملغاةُ لا تُرفع — وقد أُوقف الشريطُ على أيّ حال فلا يبقى جاريًا. */
    if (status === 'CANCELLED' || !blob || blob.size === 0) return;

    if (blob.size > MAX_FILE_BYTES) {
      toast('التسجيل أكبر من حدّ المرفقات — لم يُحفظ');
      return;
    }

    try {
      const form = new FormData();
      form.append('file', new File([blob], `call-${callId}.webm`, { type: 'audio/webm' }));
      form.append('entity', 'call');
      form.append('entityId', callId);
      const up = await uploadFile<{ id: string }>('/attachments', form);
      await api(`/callcenter/calls/${callId}/recording`, {
        method: 'PATCH',
        body: { attachmentId: up.id },
      });
      toast('حُفظ تسجيل المكالمة في ملفها');
      void queryClient.invalidateQueries({ queryKey: ['cc'] });
    } catch (e) {
      toast(e instanceof Error ? e.message : 'تعذّر حفظ التسجيل');
    }
  };

  /*
   * إغلاق الصفحة أو المُركِّب يوقف المسارات — الميكروفون لا يبقى مفتوحًا خلسة.
   * وميكروفونُ التطبيقِ مثلُه: هو في عمليةٍ أخرى لا تموت بموتِ هذه الصفحة،
   * فلو لم يُطلب إيقافُه صراحةً بقي يسمعُ بعد إغلاقِ اللوحة.
   */
  useEffect(
    () => () => {
      cleanupRef.current();
      if (viaRef.current === 'app') void deskRecorder.stop();
    },
    [],
  );

  return { session, policy, startShift, stopShift, beginCall, haltCall, finishCall };
}
